/** The audio module's public types (docs/spec/05). Re-exported from `index.ts`. */

import type { SimEvent } from '../sim/types';

export type MusicTrack = 'title' | 'hiscore' | 'gameover' | 'complete' | 'cave';
export type MusicVariant = 'classic' | 'deep';
export type UiSound = 'move' | 'select' | 'back' | 'error' | 'type';
export type Cue = 'bonusLife' | 'bonusTick' | 'pause' | 'resume' | 'start';

/** Continuous sounds, set once per scan / frame. */
export interface Ambience {
  amoeba: boolean;
  magic: boolean;
  cover: boolean;
}

/** Each 0..1. */
export interface Volumes {
  master: number;
  sfx: number;
  music: number;
}

/** Options' defaults (07 §Options: 8 / 8 / 6 out of 10). */
export const DEFAULT_VOLUMES: Volumes = { master: 0.8, sfx: 0.8, music: 0.6 };

/** A 0..1 slider as gain: squared, so the top of the range is not all the change; junk reads as 0. */
export function sliderGain(v: number): number {
  const c = Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0;
  return c * c;
}

export interface GameAudio {
  /**
   * Call on every key / click / touch: resumes the AudioContext on the first one (browsers block
   * audio before a gesture).
   */
  unlock(): void;
  /** One scan's events. listenerX is the view centre in cells, for gentle stereo pan by the event's x. */
  events(events: readonly SimEvent[], listenerX: number): void;
  ambience(a: Ambience): void;
  ui(s: UiSound): void;
  /** bonusTick: one tick of the time-bonus countdown at the exit; n counts up so the pitch can rise. */
  cue(c: Cue, n?: number): void;
  /**
   * Crossfade to a track (null = silence). variant picks e.g. `cave-deep.mp3` over `cave.mp3` when a file exists.
   * 'gameover' and 'complete' play once.
   */
  music(track: MusicTrack | null, variant?: MusicVariant): void;
  setVolumes(v: Volumes): void;
  /** Music files found in /music, for the console. */
  tracks(): string[];
}

/** Tracks that play through once and stop rather than loop. */
export function playsOnce(track: MusicTrack): boolean {
  return track === 'gameover' || track === 'complete';
}
